import React from "react";
import { FieldArray, FormikProps } from "formik";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import { FollowUpFormValues } from "src/models/FollowUp.model";
import ATMSelectValue from "src/components/UI/atoms/formFields/ATMSelectValue/ATMSelectValue";
import ATMTextArea from "src/components/UI/atoms/formFields/ATMTextArea/ATMTextArea";
import ATMRadioButton from "src/components/UI/atoms/ATMRadioButton/ATMRadioButton";
import ATMDatePicker from "src/components/UI/atoms/formFields/ATMDatePicker/ATMDatePicker";
import ATMCheckbox from "src/components/UI/atoms/formFields/ATMCheckbox/ATMCheckbox";
import ATMMultiSelect from "src/components/UI/atoms/formFields/ATMMultiSelect/ATMMultiSelect";
import useCourse from "src/hooks/useCourse";
import useCommunicationMethod from "src/hooks/useCommunicationMethods";
import { handleValidNumber } from "src/utils/validations/numberInput";
import { MdDelete } from "react-icons/md";
import { AiOutlinePlus } from "react-icons/ai";
import useDispositionLevelOne from "src/hooks/useDisPositions";
import { useGetAllDispositionLevelTwoByLevelOneIdQuery } from "src/services/DispositionLevelTwoServices";
import ATMSelect from "src/components/UI/atoms/formFields/ATMSelect/ATMSelect";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import useInquiryLabels from "src/hooks/useInquiryLabels";
import moment from "moment";

type Props = {
  formikProps: FormikProps<FollowUpFormValues>;
};

const SummaryType = [
  {
    label: "Yes",
    value: "YES",
  },
  {
    label: "No",
    value: "NO",
  },
];

const interestOptions = [
  { label: "Highly interested", value: "Highly interested" },
  { label: "Moderate", value: "Moderate" },
  { label: "Not interested", value: "Not interested" },
  { label: "NA", value: "NA" },
];

const ContactThemOption = [
  {
    label: "On Date",
    value: "ON_DATE",
  },
  {
    label: "On Event",
    value: "ON_EVENT",
  },
];

const FollowUpForm = ({ formikProps }: Props) => {
  const { values, setFieldValue, isSubmitting, handleBlur } =
    formikProps;

  //ALL COURSE
  const { course, isDataLoading: isCourseLoading } = useCourse();
  const CourseOption = course?.map((el: any) => {
    return {
      label: el?.courseName,
      value: el?._id,
      classroomFees: el?.classroomFees,
    };
  });

  //ALL Communication Method
  const { CommunicationMethod, isDataLoading: isCommunicationMethodLoading } =
    useCommunicationMethod();
  const CommunicationMethodOption = CommunicationMethod?.map((el: any) => {
    return {
      label: el?.communication_method,
      value: el?._id,
    };
  });

  //ALL DisPosition Level One
  const { DispositionLevelOne, isDataLoading: isDispositionLevelOneLoading } =
    useDispositionLevelOne();
  const DispositionLevelOneOption = DispositionLevelOne?.map((el: any) => {
    return {
      label: el?.dispositionFirstName,
      value: el?._id,
    };
  });
  //ALL DisPosition Level Two
  const {
    data: DispositionLevelTwoData,
    isLoading: DispositionLevelTwoDataIsLoading,
    isFetching: DispositionLevelTwoDataIsFetching,
  } = useGetAllDispositionLevelTwoByLevelOneIdQuery(values?.disposition_one, {
    skip: !values?.disposition_one,
  });
  const DispositionLevelTwoOption = DispositionLevelTwoData?.data?.map(
    (el: any) => {
      return {
        label: el?.dispositionSecondName,
        value: el?._id,
      };
    }
  );

  //Inquiry Label 

  const { inquiryLabels, isDataLoading: inquiryLabelsLoading } =
    useInquiryLabels();

  //Inquiry Label Option
  const InquiryLabelOptions = inquiryLabels?.map((el: any, ind: any) => {
    return {
      label: el?.inquiryLabel,
      value: el?._id,
    };
  });

  return (
    <div className="p-4 flex flex-col">
      <div className="border-l-[3px] border-primary-dark px-3 py-1 my-3 font font-medium text-[18px]  bg-white  text-primary-dark">
        Communication Details
      </div>
      <div className="grid xl:grid-cols-3 md:grid-cols-2   gap-4">
        <div className="mb-3">
          <ATMSelectValue
            name="is_communicated"
            value={values.is_communicated}
            onChange={(newValue) => {
              setFieldValue("is_communicated", newValue);
              setFieldValue("contact_them", "");
              setFieldValue("next_followUp_date", "");
              setFieldValue("next_followup_time", "");
              setFieldValue("new_batch_start", false);
              setFieldValue("followup_batch_start_course", []);
              setFieldValue("next_followup_reason", "");
              setFieldValue("next_followUp_required", "");
              setFieldValue(`communication_summary`, "");
              setFieldValue("communication_method", "");
            }}
            onBlur={handleBlur}
            label="Is Communicated"
            placeholder="Select Is Communicated"
            options={SummaryType}
          />
        </div>
        <div>
          <ATMSelectValue
            name="communication_method"
            value={values.communication_method}
            onChange={(newValue) => {
              setFieldValue("communication_method", newValue);
            }}
            onBlur={handleBlur}
            label="Communication Method"
            placeholder="Select Communication Method"
            options={CommunicationMethodOption}
            isLoading={isCommunicationMethodLoading}
          />
        </div>
        {values?.is_communicated === "YES" && (
          // Interest Level
          <div>
            <ATMSelectValue
              name="interest_level"
              value={values.interest_level}
              onChange={(newValue) => {
                setFieldValue("interest_level", newValue);
              }}
              onBlur={handleBlur}
              label="Interest Level"
              placeholder="Select Interest Level"
              options={interestOptions}
            />
          </div>
        )}
      </div>
      {values?.is_communicated === "YES" && (
        // Communication Summary
        <div className="mt-2">
          <ATMTextArea
            name={`communication_summary`}
            value={values?.communication_summary}
            onChange={(e) => setFieldValue(`communication_summary`, e)}
            label="Communication Summary"
            placeholder="Enter Communication Summary"
            onBlur={handleBlur}
          />
        </div>
      )}
      {values?.is_communicated === "YES" && (
        <div className="mt-3">
          <div className="border-l-[3px] border-primary-dark px-3 py-1 my-2 font font-medium text-[18px]  bg-white  text-primary-dark">
            Course Details
          </div>
          <div className="flex mt-4 gap-2 items-center">
            <ATMCheckbox
              extraClasses="font-semibold"
              checked={values?.course_not_clear}
              onChange={(newValue) => {
                setFieldValue("course_not_clear", newValue);
              }}
              label="Not Clear"
            ></ATMCheckbox>
            {values?.course_not_clear && (
              <ATMLoadingButton
                className="w-[130px]  bg-blue-400"
                type="button"
              >
                <a
                  href={
                    "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf"
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="text-link border-b border-link mr-3"
                >
                  {" "}
                  Open Script{" "}
                </a>
              </ATMLoadingButton>
            )}
          </div>
          <div className="mt-1">
            <FieldArray
              name="interested_course"
              render={({ push, remove }) => (
                <>
                  {values?.interested_course?.map((doc: any, index: any) => {
                    return (
                      <div key={index}>
                        <div className="flex justify-end mt-2">
                          <MdDelete
                            color="#F70913"
                            size={30}
                            className="pl-1 cursor-pointer"
                            onClick={() => {
                              showConfirmationDialog({
                                title: "Heads Up",
                                text: "Are you sure  want to Delete this Course?",
                                icon: "question",
                                showCancelButton: true,
                                next: (result) => {
                                  if (result.isConfirmed) {
                                    remove(index);
                                  }
                                },
                              });
                            }}
                          />
                        </div>
                        <div className="grid xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 gap-6">
                          <div>
                            <ATMSelect
                              name={`interested_course[${index}].course_name`}
                              value={
                                values?.interested_course?.[index]?.course_name
                              }
                              onChange={(e) => {
                                setFieldValue(
                                  `interested_course[${index}].course_name`,
                                  e
                                );
                                setFieldValue(
                                  `interested_course[${index}].fees`,
                                  e?.classroomFees
                                );
                              }}
                              options={CourseOption}
                              label="Course Name"
                              isLoading={isCourseLoading}
                            />
                          </div>
                          <div>
                            <ATMTextField
                              name={`interested_course[${index}].fees`}
                              value={values?.interested_course?.[index]?.fees}
                              onChange={(e) =>
                                handleValidNumber(e) &&
                                setFieldValue(
                                  `interested_course[${index}].fees`,
                                  e.target.value
                                )
                              }
                              label="Total Fees"
                              placeholder="Enter Total Fees"
                              onBlur={handleBlur}
                            />
                          </div>
                          <div>
                            <ATMTextField
                              name={`interested_course[${index}].Offer_Price`}
                              value={
                                values?.interested_course?.[index]?.Offer_Price
                              }
                              onChange={(e) =>
                                handleValidNumber(e) &&
                                setFieldValue(
                                  `interested_course[${index}].Offer_Price`,
                                  e.target.value
                                )
                              }
                              label="Offer Price"
                              placeholder="Enter Offer Price"
                              onBlur={handleBlur}
                            />
                          </div>
                          <div>
                            <ATMTextField
                              name={`interested_course[${index}].remark`}
                              value={values?.interested_course?.[index]?.remark}
                              onChange={(e) =>
                                setFieldValue(
                                  `interested_course[${index}].remark`,
                                  e.target.value
                                )
                              }
                              label="Remark"
                              placeholder="Enter Remark"
                              onBlur={handleBlur}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className="flex justify-end my-2">
                    <p
                      className="text-link font-semibold text-base text-[16px] max-[1024px]:text-sm flex items-center cursor-pointer"
                      onClick={() =>
                        push({
                          course_name: "",
                          fees: "",
                          Offer_Price: "",
                          remark: "",
                        })
                      }
                    >
                      <AiOutlinePlus
                        size={18}
                        color="#0064C4"
                        className="mr-2"
                      />
                      Add Course
                    </p>
                  </div>
                </>
              )}
            />
          </div>
        </div>
      )}

      <div className="border-l-[3px] border-primary-dark px-3 py-1 my-2 font font-medium text-[18px]  bg-white  text-primary-dark">
        Next FollowUp Details
      </div>

      <div className="grid xl:grid-cols-3 md:grid-cols-2 gap-8">

        <div>
          <ATMSelectValue
            name="next_followUp_required"
            value={values.next_followUp_required}
            onChange={(newValue) => {
              setFieldValue("next_followUp_required", newValue);
            }}
            onBlur={handleBlur}
            label="Next FollowUp Required"
            placeholder="Select Next FollowUp Required"
            options={SummaryType}
          />
        </div>

        {values?.next_followUp_required === "YES" && (
          <div>
            <ATMRadioButton
              label="Contact Them"
              name="contact_them"
              value={values?.contact_them || ""}
              className="mt-1  grid-cols-3"
              options={ContactThemOption}
              onChange={(e) => {
                setFieldValue("contact_them", e);
                setFieldValue("next_followUp_date", "");
                setFieldValue("next_followup_time", "");
                setFieldValue("new_batch_start", false);
                setFieldValue("followup_batch_start_course", []);
              }}
            />
          </div>
        )}
      </div>


      <div className="grid xl:grid-cols-3  md:grid-cols-2 gap-6 mt-3">
        {values?.contact_them === "ON_DATE" &&
          values?.next_followUp_required === "YES" && (
            <ATMDatePicker
              name="next_followUp_date"
              value={values?.next_followUp_date || null}
              onChange={(newValue) =>
                setFieldValue("next_followUp_date", newValue)
              }
              label="Next Followup  Date "
            />
            //   <ATMTextField
            //   type="date"
            //   name="next_followUp_date"
            //   value={values?.next_followUp_date}
            //   onChange={(e) => {
            //     console.log(e.target.value ,"e")
            //     let date = moment(e.target.value, "DD MMM yyyy");
            //     setFieldValue("next_followUp_date", e.target.value);
            //   }}
            //   label="Next Followup Date"
            //   onBlur={handleBlur}
            //   required
            //   placeholder="Please Next Followup Date"
            // />
          )}
        {values?.contact_them === "ON_DATE" &&
          values?.next_followUp_required === "YES" && (
            <ATMTextField
              type="time"
              name="next_followup_time"
              value={moment(values?.next_followup_time, "HH:mm").format("HH:mm")}
              onChange={(e) => {
                let time = moment(e.target.value, "HH:mm");
                setFieldValue("next_followup_time", time);
              }}
              label="Next Followup Time"
              onBlur={handleBlur}
              required
              placeholder="Please Next Followup Time"
            />
          )}
      </div>

      <div>
        {values?.contact_them === "ON_EVENT" &&
          values?.next_followUp_required === "YES" && (
            <div className="flex  items-center gap-4 mt-4">
              <ATMCheckbox
                name="new_batch_start"
                extraClasses="font-semibold"
                checked={values?.new_batch_start}
                onChange={(newValue) => {
                  setFieldValue("new_batch_start", newValue);
                }}
                label="When New Batch Start For"
              />
              <div className="w-[400px]">
                <ATMMultiSelect
                  name="followup_batch_start_course"
                  value={values?.followup_batch_start_course}
                  onChange={(newValue) =>
                    setFieldValue("followup_batch_start_course", newValue)
                  }
                  onBlur={handleBlur}
                  placeholder="Select Course"
                  isLoading={isCourseLoading}
                  options={CourseOption}
                />
              </div>
            </div>
          )}
      </div>

      <div>
        {values?.next_followUp_required === "NO" && (
          <div className="my-5">
            <ATMTextArea
              name="next_followup_reason"
              value={values?.next_followup_reason}
              onChange={(value) => {
                setFieldValue("next_followup_reason", value);
              }}
              label="Reason"
              placeholder="Enter Reason"
              onBlur={handleBlur}
            />
          </div>
        )}
      </div>
      {values?.is_communicated === "YES" && (
        <div className="border-l-[3px] border-primary-dark px-3 py-1 my-2 font font-medium text-[18px]  bg-white  text-primary-dark">
          Disposition Details
        </div>
      )}
      <div className="grid xl:grid-cols-3 md:grid-cols-2 gap-6">
        <div>
          <ATMSelectValue
            name="disposition_one"
            value={values?.disposition_one}
            onChange={(newValue) => {
              setFieldValue("disposition_one", newValue);
              setFieldValue("disposition_two", "");
            }}
            onBlur={handleBlur}
            label="Disposition One"
            placeholder="Select Disposition One"
            options={DispositionLevelOneOption}
            isLoading={isDispositionLevelOneLoading}
          />
        </div>
        <div>
          <ATMSelectValue
            name="disposition_two"
            value={values?.disposition_two}
            onChange={(newValue) => {
              setFieldValue("disposition_two", newValue);
            }}
            onBlur={handleBlur}
            label="Disposition Two"
            placeholder="Select Disposition Two"
            options={DispositionLevelTwoOption}
            isLoading={
              DispositionLevelTwoDataIsLoading ||
              DispositionLevelTwoDataIsFetching
            }
          />
        </div>
        {/* Inquiry Label */}
        <div className="">
          <ATMSelect
            name="inquiryLabel"
            value={values?.inquiryLabel}
            onChange={(value) => setFieldValue("inquiryLabel", value)}
            options={InquiryLabelOptions}
            label="Inquiry Label"
            isLoading={inquiryLabelsLoading}
          />
        </div>
      </div>

      <div className="flex justify-end mt-2">
        <ATMLoadingButton
          className="flex justify-end w-[90px] px-8"
          isLoading={isSubmitting}
          type="submit"
        >
          Save
        </ATMLoadingButton>
      </div>
    </div>
  );
};

export default FollowUpForm;
